import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { cachedGet } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const Stars = ({ rating }) => (
  <div className="flex gap-0.5" aria-label={`${rating} de 5 estrelas`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`}
      />
    ))}
  </div>
);

export const ReviewsSection = ({ title = "O que nossos clientes dizem" }) => {
  const settings = useSettings();
  const enabled = (settings.features || {}).reviews !== false;
  const dragOn = (settings.animations || {}).enabled !== false && (settings.animations || {}).gallery_drag !== false;
  const [reviews, setReviews] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", dragFree: dragOn });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!enabled) return;
    // prioridade: avaliações da loja marcadas como destaque; sem destaques, as mais recentes (até 3+)
    cachedGet("/reviews?product_id=&featured=true", 300000)
      .then((data) => {
        if (data.length > 0) setReviews(data.slice(0, 6));
        else return cachedGet("/reviews", 300000).then((d2) => setReviews(d2.slice(0, 6)));
      })
      .catch(() => {});
  }, [enabled]);

  // sem avaliações cadastradas (ou recurso desativado), a seção não aparece na loja
  if (!enabled || reviews.length === 0) return null;

  return (
    <section className="container-store py-14" data-testid="reviews-section">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            {title}
          </h2>
          <p className="text-gray-500 mt-1">Avaliações de quem já comprou com a HG Móveis</p>
        </div>
        <div className="hidden sm:flex gap-2">
          <button
            onClick={scrollPrev}
            aria-label="Avaliações anteriores"
            className="h-10 w-10 rounded-full border border-gray-200 bg-white hover:border-brand hover:text-brand flex items-center justify-center transition-colors"
            data-testid="reviews-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Próximas avaliações"
            className="h-10 w-10 rounded-full border border-gray-200 bg-white hover:border-brand hover:text-brand flex items-center justify-center transition-colors"
            data-testid="reviews-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {reviews.map((review) => (
            <article
              key={review.id}
              className="flex-[0_0_85%] sm:flex-[0_0_46%] lg:flex-[0_0_31.5%] min-w-0 bg-white border border-gray-100 rounded-xl p-5 shadow-sm"
              data-testid={`review-card-${review.id}`}
            >
              <Quote className="w-6 h-6 text-brand-light fill-brand-light" />
              <div className="mt-2">
                <Stars rating={review.rating} />
              </div>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-4">
                "{review.text}"
              </p>
              <footer className="mt-4 pt-3 border-t border-gray-50">
                <p className="font-semibold text-sm text-gray-900">{review.name}</p>
                {review.city && <p className="text-xs text-gray-400">{review.city}</p>}
              </footer>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
